import { PlannedBreakFragment } from 'generated/resources/graphql'
import map from 'lodash/map'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'

import { timeToStringWithTimezone } from 'modules/core/lib/dates'

import PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { SYMBOLIC_MIXED_VALUE, type Shift, type View } from 'modules/shift_management/lib/types'

export type IdsTypes = string[] | (string | null | undefined)

export type DaysByRow = Record<string, string[]>

export type ModalStateType = 'fetchingEmployees' | 'showDetails'

interface FormDataReturnProps {
  name: string | undefined | null
  startAt: string | undefined | null
  endAt: string | undefined | null
  extraHours: boolean | null
  notes: string | undefined | null
  locationId: string | undefined | null
  workAreaId: string | undefined | null
  // EmployeeSelectorInput uses kebab case
  employee_id: string | undefined | null
}

export const formData = (shifts: Shift[], view: View): FormDataReturnProps => {
  // We are not handling names in the old edit modal which
  // so templateWeeks are not being sent as props to this
  // function if it is being used by the old modal
  const getDisplayNames = shifts.map((shift) => shift.displayName)
  const displayNames = map(uniq(getDisplayNames))
  const names = map(uniqBy(shifts, 'name'), 'name')
  const startTimes = map(uniqBy(shifts, 'startTime'), 'startTime')
  const endTimes = map(uniqBy(shifts, 'endTime'), 'endTime')
  const extraHours = map(uniqBy(shifts, 'extraHours'), (s) => Boolean(s.extraHours))
  const notes = map(uniqBy(shifts, 'notes'), 'notes')
  const shiftLocations = map(uniqBy(shifts, 'locationId'), 'locationId')
  const employeesFromShifts = map(uniqBy(shifts, 'employee'), 'employee')
  const employeeLocations = map(uniqBy(employeesFromShifts, 'locationId'), 'locationId')
  const locations = view.isByAreas && !shiftLocations[0] ? employeeLocations : shiftLocations
  const workAreas = map(uniqBy(shifts, 'workAreaId'), 'workAreaId')
  const employeesId = map(uniqBy(employeesFromShifts, 'id'), 'id')

  // TODO: remove this logic when template + template week is
  // incorporated in the name in the backend
  const mixedNameOrTitle = names.length > 1 || displayNames.length > 1
  const sameName = names.length === 1 && names[0] !== null
  const finalName = mixedNameOrTitle ? null : sameName ? names[0] : displayNames[0]

  return {
    name: finalName,
    startAt: startTimes.length > 1 ? null : startTimes[0],
    endAt: endTimes.length > 1 ? null : endTimes[0],
    extraHours: extraHours.length > 1 ? null : Boolean(extraHours[0]),
    notes: notes.length > 1 ? null : notes[0],
    locationId: locations.length > 1 ? SYMBOLIC_MIXED_VALUE : locations[0],
    workAreaId: workAreas.length > 1 ? SYMBOLIC_MIXED_VALUE : workAreas[0],
    employee_id: employeesId.length === 1 ? employeesId[0] : null,
  }
}

export const hasMixedValues = (
  initialAttributes: FormDataReturnProps,
  uniqNotes: (string | null)[]
) => {
  const mixedValues =
    initialAttributes.name === null ||
    !initialAttributes.startAt ||
    !initialAttributes.endAt ||
    uniqNotes.length > 1 ||
    initialAttributes.extraHours === null ||
    initialAttributes.locationId === SYMBOLIC_MIXED_VALUE ||
    initialAttributes.workAreaId === SYMBOLIC_MIXED_VALUE

  return mixedValues
}

// We don't show the option to reassign when there is a mix of default
// shifts or templates so only with 1 type of those, we will need the id for the params
export const getDefaultShiftId = (shifts: Shift[]): string | null | undefined => {
  const shiftDefaultIds = uniq(shifts.map((s) => s.defaultShiftId))
  const defaultId = shiftDefaultIds.length === 1 ? shiftDefaultIds[0] : null

  return defaultId
}

export const getTemplateId = (shifts: Shift[]): string | null | undefined => {
  const templateWeekIds = uniq(shifts.map((s) => s.templateWeekId))
  const templateId = templateWeekIds.length === 1 ? templateWeekIds[0] : null

  return templateId
}

export const plannedBreakForm = (
  plannedBreak: PlannedBreakFragment,
  timezone: string | null | undefined,
  isTimeFormat24H: boolean
): PlannedBreakForm => {
  return new PlannedBreakForm(
    {
      id: plannedBreak.id,
      start_at: plannedBreak.startAt
        ? timeToStringWithTimezone(plannedBreak.startAt, timezone ?? '')
        : '',
      end_at: plannedBreak.endAt
        ? timeToStringWithTimezone(plannedBreak.endAt, timezone ?? '')
        : '',
      break_type: plannedBreak.breakType,
      duration: plannedBreak.duration,
      'break_configuration.id': plannedBreak.breakConfiguration?.id,
      'break_configuration.name': plannedBreak.breakConfiguration?.name,
      'break_configuration.paid': plannedBreak.breakConfiguration?.paid,
      'break_configuration.archived': plannedBreak.breakConfiguration?.archived,
    },
    isTimeFormat24H
  )
}
